<script lang="ts">
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';

  export let showApplyForm = false;
  let tabs: HTMLElement[] = [];
  let selectedIndex = 0;

  const responsiveVars = new ResponsiveVars()
    .addFluid('tabs-mt', toFluidVarInput(67, 80))
    .addFluid('tabs-mb', toFluidVarInput(60, 62))
    .addFluid('font-size', toFluidVarInput(18, 18))
    .addFluid('padding', toFluidVarInput(9, 9))
    .toCSS();

  const handleTabFocus = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      return;
    }

    e.preventDefault();
    tabs[selectedIndex].tabIndex = -1;

    if (e.key === 'ArrowLeft') {
      selectedIndex = selectedIndex === 0 ? tabs.length - 1 : selectedIndex - 1;
    } else if (e.key === 'ArrowRight') {
      selectedIndex = selectedIndex === tabs.length - 1 ? 0 : selectedIndex + 1;
    }

    tabs[selectedIndex].tabIndex = 0;
    tabs[selectedIndex].focus();
  };

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);
</script>

<div class={cx('tab-section', responsiveVars)}>
  <div class="tab-container" role="tablist" aria-labelledby="job-application-tabs" dir="ltr">
    <button
      bind:this={tabs[0]}
      id="job-details-tab"
      class="tab-button primary-button"
      class:selected={!showApplyForm}
      type="button"
      role="tab"
      aria-selected={!showApplyForm}
      aria-controls="job-details-content"
      tabindex={!showApplyForm ? 0 : -1}
      on:click={() => (showApplyForm = false)}
      on:keydown={handleTabFocus}
      data-dd-action-name="Job Details tab button"
    >
      {#if brandReskinEnabled}
        <FontSizeDecorator>Job Details</FontSizeDecorator>
      {:else}
        Job Details
      {/if}
    </button>

    <button
      bind:this={tabs[1]}
      id="apply-form-tab"
      class="tab-button primary-button"
      class:selected={showApplyForm}
      type="button"
      role="tab"
      aria-selected={showApplyForm}
      aria-controls="apply-form-content"
      tabindex={showApplyForm ? 0 : -1}
      on:click={() => (showApplyForm = true)}
      on:keydown={handleTabFocus}
      data-dd-action-name="Apply tab button"
    >
      {#if brandReskinEnabled}
        <FontSizeDecorator>Apply</FontSizeDecorator>
      {:else}
        Apply
      {/if}
    </button>
  </div>
</div>

<style lang="scss">
  .tab-section {
    display: flex;
    justify-content: center;
    margin-top: var(--tabs-mt);
    margin-bottom: var(--tabs-mb);
  }

  .tab-container {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    border: 1px solid black;
    width: 100%;
    padding: var(--twist-core-spacing-2);
    gap: var(--twist-core-spacing-3);
    border-radius: 1000px;
  }

  .primary-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    border-radius: 1000px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    font-family: var(--font-family-primary);
    padding: var(--padding);
    font-size: var(--font-size);
    font-weight: 700;
    line-height: 120%;

    &:focus {
      outline: none;
    }

    &#job-details-tab {
      &.selected,
      &:focus,
      &:hover {
        background-color: black;
        color: white;
      }
    }

    &#apply-form-tab {
      &.selected,
      &:focus,
      &:hover {
        background-color: var(
          --theme-button-primary-button-default-background-color,
          var(--twist-color-gray-100)
        );
        color: var(
          --theme-button-primary-button-default-foreground-color,
          var(--twist-color-gray-0)
        );
      }

      &:active {
        opacity: 60%;
      }
    }
  }
</style>
